import { isMacro } from 'spw-components/worksheet/utils';
import { captureOptions } from 'spw-export/constants';
import { IAppState } from 'spw-redux/reducer';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import {
  FILE_TYPES,
  NOTE_GENERAL_CATEGORY,
  SHARED_SPACES_CATEGORY,
} from 'spw-constants';
import * as constants from 'spw-constants';
import {
  IBuilding,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import {
  ReportAttributes,
  ReportEntry,
  ScenarioExportable,
} from 'spw-redux/modules/reports/types';
import {
  REPORT_TYPES,
  SCENARIO_EXPORTABLE,
} from 'spw-redux/modules/reports/constants';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';
import { PlainProgramScenario } from '../../../models/Scenario';
import { PlainWorksheet } from '../../../models/Worksheet';

export const getZipNameOrExportablePrefix = (
  currentScenario: PlainProgramScenario,
  building: IBuilding
) => {
  let buildingOrFloorPrefix = 'Bldg';

  if (isMacro(currentScenario)) {
    buildingOrFloorPrefix = 'Macro';
  } else if (currentScenario?.floorId) {
    const floor = building?.floors?.find(
      (f) => f.id === currentScenario.floorId
    );
    if (floor) {
      buildingOrFloorPrefix = floor.name;
    }
  }
  return buildingOrFloorPrefix;
};

export const getOpenClosed = (
  currentScenario: PlainProgramScenario,
  currentWorksheet: PlainWorksheet,
  isOpen: boolean
) => {
  if (
    currentScenario.isDetailed ||
    (isTotalProgram(currentScenario) &&
      currentScenario.linkedScenarios[0].isDetailed)
  ) {
    return isOpen ? 'Open' : 'Closed';
  }

  if (isTotalProgram(currentScenario)) {
    return currentWorksheet.rows.length > 0 ? 'Open' : 'Closed';
  }

  return currentWorksheet.hasNestedRows ? 'Open' : 'Closed';
};

export const getReportRequestEntry = (
  {
    exportableType,
    entityId,
    variants,
    format,
  }: {
    exportableType: ScenarioExportable;
    entityId: number;
    variants?: {
      isOpen?: boolean;
      showNotes?: boolean;
      category?: string;
      showRsf?: boolean;
      shouldShowClientFacingNames?: boolean;
      showDecimals?: boolean;
      measurementSystem?: MeasurementSystem;
    };
    format: string;
  },
  appState: IAppState
): ReportEntry => {
  const { currentScenario } = appState.calculatorReducer;
  const entityType = isTotalProgram(currentScenario)
    ? 'TOTAL_PROGRAM'
    : 'SCENARIO';

  const reportEntry: ReportEntry = {
    entityType,
    entityId,
    exportable: null,
    variants,
    screenshotsOpts: [
      {
        format,
        width: 0,
      },
    ],
  };

  switch (exportableType) {
    case SCENARIO_EXPORTABLE.WORKSHEET: {
      return {
        ...reportEntry,
        exportable: SCENARIO_EXPORTABLE.WORKSHEET,
        screenshotsOpts: [
          {
            ...reportEntry.screenshotsOpts[0],
            width: captureOptions.programmingWorksheet.width,
          },
        ],
      };
    }
    case SCENARIO_EXPORTABLE.SUMMARY_CALCULATIONS: {
      return {
        ...reportEntry,
        exportable: SCENARIO_EXPORTABLE.SUMMARY_CALCULATIONS,
        screenshotsOpts: [
          {
            ...reportEntry.screenshotsOpts[0],
            width: captureOptions.summaryCalculations.width,
          },
        ],
      };
    }
    case SCENARIO_EXPORTABLE.PROGRAM_SUMMARY: {
      return {
        ...reportEntry,
        exportable: SCENARIO_EXPORTABLE.PROGRAM_SUMMARY,
        screenshotsOpts: [
          {
            ...reportEntry.screenshotsOpts[0],
            width: captureOptions.singlePageSummary.width,
          },
        ],
      };
    }
    case SCENARIO_EXPORTABLE.PROGRAM_SUMMARY_ONE_PAGER: {
      return {
        ...reportEntry,
        exportable: SCENARIO_EXPORTABLE.PROGRAM_SUMMARY,
        screenshotsOpts: [
          {
            ...reportEntry.screenshotsOpts[0],
            width: captureOptions.singlePageSummary.width,
          },
        ],
      };
    }
    case SCENARIO_EXPORTABLE.SEAT_COUNT_ANALYSIS: {
      return {
        ...reportEntry,
        exportable: SCENARIO_EXPORTABLE.SEAT_COUNT_ANALYSIS,
        screenshotsOpts: [
          {
            ...reportEntry.screenshotsOpts[0],
            width: captureOptions.scenario.width,
          },
        ],
      };
    }
    case SCENARIO_EXPORTABLE.PROGRAM_NOTES: {
      return {
        ...reportEntry,
        exportable: SCENARIO_EXPORTABLE.PROGRAM_NOTES,
        screenshotsOpts: [
          {
            ...reportEntry.screenshotsOpts[0],
            width: captureOptions.worksheetNote.width,
          },
        ],
      };
    }
    default:
      return null;
  }
};

const worksheetRequestEntry = (
  state: IAppState,
  entityId: number,
  format: string,
  variants?: {
    isOpen?: boolean;
    showNotes?: boolean;
    shouldShowClientFacingNames?: boolean;
    showDecimals?: boolean;
    measurementSystem?: MeasurementSystem;
    category?: string;
  }
) => {
  return getReportRequestEntry(
    {
      exportableType: SCENARIO_EXPORTABLE.WORKSHEET,
      entityId,
      variants,
      format,
    },
    state
  );
};

export const getReportRequest = (
  {
    reportType,
    entityId,
    category,
    isOpen,
    shouldShowClientFacingNames,
    format,
    showDecimals,
    measurementSystem,
  }: ReportAttributes,
  zipName: string,
  state: IAppState
): { zipName: string; reportEntries: ReportEntry[]; projectId: number } => {
  const { currentScenario, worksheet } = state.calculatorReducer;
  const { spaceTypeCategories } = state.projectsReducer.currentProject;
  const projectId = state.projectsReducer.currentProject.id;
  const newZipName = zipName.replace(/[/<>:\\|?*]/g, '-');
  const fileFormat = format.toUpperCase();

  const useNewFormat = [
    'total-program',
    'building-headcount-seatcount-departments',
  ].includes(currentScenario.calculatorType);

  const projectSpaceTypeCategories = spaceTypeCategories.filter(
    (projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
      const {
        category: defaultCategory,
      } = projectSpaceTypeCategory.defaultSpaceTypeCategory;
      return (
        defaultCategory === constants.INDIVIDUAL_WORKSPACES_CATEGORY ||
        worksheet.rowsByCategory[defaultCategory]?.length
      );
    }
  );

  switch (reportType) {
    case REPORT_TYPES.WORKSHEET: {
      return {
        projectId,
        zipName: newZipName,
        reportEntries:
          fileFormat === FILE_TYPES.PNG && !currentScenario.isShared
            ? projectSpaceTypeCategories.map(
                (projectSpaceTypeCategory: IProjectSpaceTypeCategory) =>
                  worksheetRequestEntry(state, entityId, fileFormat, {
                    isOpen,
                    category:
                      projectSpaceTypeCategory.defaultSpaceTypeCategory
                        .category,
                    shouldShowClientFacingNames,
                    showDecimals,
                    measurementSystem,
                  })
              )
            : [
                worksheetRequestEntry(state, entityId, fileFormat, {
                  isOpen,
                  shouldShowClientFacingNames,
                  showDecimals,
                  measurementSystem,
                }),
              ],
      };
    }
    case REPORT_TYPES.WORKSHEET_WITH_NOTES: {
      const openClosed = getOpenClosed(currentScenario, worksheet, isOpen);

      return {
        projectId,
        zipName: newZipName?.replace(
          'Worksheet',
          useNewFormat
            ? `Worksheet ${openClosed} with Notes`.trim()
            : `Notes with Worksheet ${openClosed}`
        ),
        reportEntries:
          fileFormat === FILE_TYPES.PNG && !currentScenario.isShared
            ? projectSpaceTypeCategories.map(
                (projectSpaceTypeCategory: IProjectSpaceTypeCategory) =>
                  worksheetRequestEntry(state, entityId, fileFormat, {
                    isOpen,
                    category:
                      projectSpaceTypeCategory.defaultSpaceTypeCategory
                        .category,
                    shouldShowClientFacingNames,
                    showDecimals,
                    showNotes: true,
                    measurementSystem,
                  })
              )
            : [
                worksheetRequestEntry(state, entityId, fileFormat, {
                  isOpen,
                  showNotes: true,
                  shouldShowClientFacingNames,
                  showDecimals,
                  measurementSystem,
                }),
              ],
      };
    }
    case REPORT_TYPES.WORKSHEET_AND_SUMMARY_CALCULATIONS: {
      const openClosed = getOpenClosed(currentScenario, worksheet, isOpen);

      return {
        projectId,
        zipName: newZipName?.replace(
          'Worksheet',
          useNewFormat
            ? `Worksheet ${openClosed} and Summary Calcs`
            : `Summary and Worksheet ${openClosed}`
        ),
        reportEntries: [
          worksheetRequestEntry(state, entityId, fileFormat, {
            isOpen,
            shouldShowClientFacingNames,
            showDecimals,
            measurementSystem,
          }),
          getReportRequestEntry(
            {
              exportableType: SCENARIO_EXPORTABLE.SUMMARY_CALCULATIONS,
              entityId,
              format: fileFormat,
              variants: {
                showDecimals,
                measurementSystem,
              },
            },
            state
          ),
        ],
      };
    }

    case REPORT_TYPES.SEAT_COUNT_ANALYSIS: {
      return {
        projectId,
        zipName,
        reportEntries: [
          getReportRequestEntry(
            {
              exportableType: SCENARIO_EXPORTABLE.SEAT_COUNT_ANALYSIS,
              entityId,
              format: fileFormat,
            },
            state
          ),
        ],
      };
    }

    case REPORT_TYPES.SUMMARY_CALCULATIONS: {
      return {
        projectId,
        zipName,
        reportEntries: [
          getReportRequestEntry(
            {
              exportableType: SCENARIO_EXPORTABLE.SUMMARY_CALCULATIONS,
              entityId,
              format: fileFormat,
            },
            state
          ),
        ],
      };
    }
    case REPORT_TYPES.NOTES_BY_CATEGORY:
    case REPORT_TYPES.ALL_NOTES: {
      const reportEntries: ReportEntry[] = [];

      if (reportType === REPORT_TYPES.ALL_NOTES) {
        const categories: string[] = [];

        if (currentScenario.isShared) {
          categories.push(SHARED_SPACES_CATEGORY);
        }

        spaceTypeCategories
          .filter((projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
            if (currentScenario.isShared)
              return (
                projectSpaceTypeCategory.defaultSpaceTypeCategory.category ===
                constants.INDIVIDUAL_WORKSPACES_CATEGORY
              );

            return true;
          })
          .forEach((projectSpaceTypeCategory: IProjectSpaceTypeCategory) => {
            categories.push(
              projectSpaceTypeCategory.defaultSpaceTypeCategory.category
            );
          });

        categories.push(NOTE_GENERAL_CATEGORY);

        categories.forEach((noteCategory: string) => {
          reportEntries.push(
            getReportRequestEntry(
              {
                exportableType: SCENARIO_EXPORTABLE.PROGRAM_NOTES,
                entityId,
                format: fileFormat,
                variants: {
                  category: noteCategory,
                },
              },
              state
            )
          );
        });
      } else {
        reportEntries.push(
          getReportRequestEntry(
            {
              exportableType: SCENARIO_EXPORTABLE.PROGRAM_NOTES,
              entityId,
              format: fileFormat,
              variants: {
                category,
              },
            },
            state
          )
        );
      }

      return {
        projectId,
        zipName,
        reportEntries,
      };
    }
    default:
      return { zipName: '', reportEntries: [], projectId };
  }
};
